/**
 * /hotels/search — Hotels availability results.
 *
 * Two view modes: grid (default) and swipe (side-swipe deck for the
 * "browse or swipe" UX). Client-side filters (min category, board
 * type, max price) narrow the returned set without re-querying HB.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Star, Grid3x3, Layers, ChevronLeft, ChevronRight,
  Bed, X as XIcon, SlidersHorizontal, Clock, RefreshCw,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PoweredByHotelbeds from "@/components/hotels/PoweredByHotelbeds";
import PhaseFourFiltersSheet from "@/components/PhaseFourFiltersSheet";

const BOARD_LABELS = { RO: "Room Only", BB: "Breakfast", HB: "Half Board", FB: "Full Board", AI: "All-Inclusive" };

function categoryStars(catCode) {
  if (!catCode) return 0;
  const m = /^(\d)/.exec(catCode);
  return m ? Number(m[1]) : 0;
}

function pickCheapestRate(hotel) {
  const rates = (hotel.rooms || []).flatMap((r) => r.rates || []);
  if (!rates.length) return null;
  return rates.reduce((best, r) => {
    const price = Number(r.net || r.sellingRate || Infinity);
    if (!best || price < Number(best.net || best.sellingRate || Infinity)) return r;
    return best;
  }, null);
}

function firstCancellationSummary(rate) {
  const cps = rate?.cancellationPolicies || [];
  if (!cps.length) return null;
  const first = cps[0];
  if (!first.from) return null;
  const dt = new Date(first.from);
  if (isNaN(dt.getTime())) return null;
  return `Free cancellation until ${dt.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

function HotelCard({ hotel, onClick, testIdSuffix }) {
  const cheapest = pickCheapestRate(hotel);
  const stars = categoryStars(hotel.categoryCode);
  const cancel = firstCancellationSummary(cheapest);
  const board = cheapest?.boardCode ? (BOARD_LABELS[cheapest.boardCode] || cheapest.boardCode) : null;
  return (
    <button
      type="button"
      data-testid={`hotels-result-card-${hotel.code}`}
      onClick={onClick}
      className="group text-left w-full h-full flex flex-col rounded-2xl border-2 border-tb-navy/10 bg-white overflow-hidden shadow-sm hover:border-tb-navy hover:shadow-md transition"
    >
      <div className="relative aspect-[4/3] bg-white overflow-hidden">
        {hotel.thumbnailUrl ? (
          <img
            src={hotel.thumbnailUrl}
            alt={hotel.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, rgba(15,39,64,0.9) 0%, rgba(26,58,95,0.75) 60%, rgba(230,184,74,0.35) 100%)",
            }}
          >
            <Bed className="w-14 h-14 text-white/70" strokeWidth={1.4} />
          </div>
        )}
        <div className="absolute top-2 left-2">
          <PoweredByHotelbeds size="xs" />
        </div>
        {stars > 0 && (
          <div className="absolute top-2 right-2 inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-white/90 border border-tb-navy/20">
            {Array.from({ length: stars }).map((_, i) => (
              <Star key={i} className="w-3 h-3 text-tb-gold fill-tb-gold" />
            ))}
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <h3
          className="text-tb-navy text-lg leading-tight line-clamp-2 font-semibold"
          style={{ fontFamily: '"Playfair Display", serif' }}
        >
          {hotel.name}
        </h3>
        <div className="mt-1 font-hand text-tb-navy/60 text-xs">
          {[hotel.zoneName, hotel.destinationName].filter(Boolean).join(" · ")}
        </div>
        <div className="mt-auto pt-3 flex items-end justify-between gap-2">
          <div>
            <div className="font-hand text-tb-navy/50 text-[11px] uppercase tracking-widest">
              {hotel.minRate != null ? "from" : ""}
            </div>
            <div
              className="text-tb-navy text-xl leading-none font-semibold"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              {hotel.minRate != null
                ? `${hotel.minRate.toFixed(2)} ${hotel.currency || ""}`
                : "View details"}
            </div>
            {board && (
              <div className="mt-0.5 font-hand text-tb-navy/55 text-[11px]">{board}</div>
            )}
          </div>
          {cancel && (
            <div className="px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 font-hand text-[10px] leading-tight max-w-[45%] text-right">
              {cancel}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

function SwipeDeck({ hotels, onOpen }) {
  const [idx, setIdx] = useState(0);
  const [direction, setDirection] = useState(0);
  const current = hotels[idx];
  const remaining = hotels.length - idx;
  const advance = (dir) => {
    setDirection(dir);
    setTimeout(() => setIdx((i) => Math.min(i + 1, hotels.length)), 150);
  };
  if (!current) {
    return (
      <div data-testid="hotels-swipe-empty" className="text-center py-16">
        <p className="font-hand text-tb-navy/60">You&apos;ve reviewed every hotel. Switch to grid to compare them all.</p>
      </div>
    );
  }
  return (
    <div className="relative">
      <div className="text-center font-hand text-tb-navy/55 text-xs uppercase tracking-widest mb-2">
        {remaining} of {hotels.length} left · swipe or tap
      </div>
      <div className="relative max-w-md mx-auto" style={{ minHeight: 480 }} data-testid="hotels-swipe-deck">
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={current.code}
            custom={direction}
            initial={{ opacity: 0, scale: 0.94, x: direction * 40 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * -300, rotate: direction * -6 }}
            transition={{ duration: 0.18 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            onDragEnd={(_, info) => {
              if (info.offset.x < -80) advance(-1);
              else if (info.offset.x > 80) { onOpen(current); }
            }}
            className="absolute inset-0"
          >
            <HotelCard hotel={current} onClick={() => onOpen(current)} testIdSuffix="swipe" />
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="mt-5 flex items-center justify-center gap-4">
        <button
          type="button"
          data-testid="hotels-swipe-skip"
          onClick={() => advance(-1)}
          className="w-14 h-14 rounded-full border-2 border-tb-navy/25 text-tb-navy/70 flex items-center justify-center bg-white hover:border-red-400 hover:text-red-500 tb-tap"
        >
          <XIcon className="w-5 h-5" />
        </button>
        <button
          type="button"
          data-testid="hotels-swipe-open"
          onClick={() => onOpen(current)}
          className="px-6 py-3 rounded-full bg-tb-navy-deep text-tb-gold-bright border-2 border-tb-gold font-hand text-base tb-tap hover:brightness-110"
        >
          View this hotel
        </button>
      </div>
    </div>
  );
}

export default function HotelsResultsPage() {
  const nav = useNavigate();
  const location = useLocation();
  // Phase 5 issue-fix (2026-07-27): the "back" pill previously
  // hard-coded `nav("/hotels")` which broke the natural back stack
  // for anyone arriving from `/explore`, the header search modal,
  // etc. Now we read `location.state.from` (set by the various
  // entry points) and fall back to `/explore` — the canonical
  // Hotelbeds entry point — when the state is absent (e.g.
  // deep-link, share, refresh).
  const backTarget = location.state?.from || "/explore";
  const [sp] = useSearchParams();
  const destinationCode = sp.get("destinationCode");
  const destinationName = sp.get("destinationName") || destinationCode;
  // Phase 5 issue-fix (2026-07-28) — Gap 2: lenient deep-link
  // parsing. Missing / malformed `checkIn`/`checkOut`/`occupancies`
  // used to short-circuit into a "Missing search parameters" dead
  // end. Now we default to today+30 → today+34 (matches the
  // Explore hero search defaults) + 2 adults + 1 room, so the
  // availability call fires, and the 429 fallback still surfaces
  // the cached grid on quota-locked days. `destinationCode` remains
  // truly required — without it we can't hit HB at all.
  const todayPlus = (d) => {
    const t = new Date(); t.setDate(t.getDate() + d);
    return t.toISOString().slice(0, 10);
  };
  const checkIn = sp.get("checkIn") || todayPlus(30);
  const checkOut = sp.get("checkOut") || todayPlus(34);
  const occupancies = useMemo(() => {
    const raw = sp.get("occupancies");
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      } catch { /* fall through to default */ }
    }
    return [{
      rooms: 1, adults: 2, children: 0,
      paxes: [{ type: "AD", age: 30 }, { type: "AD", age: 30 }],
    }];
  }, [sp]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [rateLimited, setRateLimited] = useState(false);
  // Phase 5 issue-fix (2026-07-28): cached-hotel fallback surfaced
  // when a live availability call 429s. Populated from
  // `GET /api/hotels/featured?destinationCode=…` — no live rates
  // (`minRate: null`), but the user can still explore properties,
  // read details, and test the booking form flow. Cleared on Retry.
  const [fallbackHotels, setFallbackHotels] = useState([]);
  const [fallbackDestMissed, setFallbackDestMissed] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [data, setData] = useState({ hotels: [] });
  const [view, setView] = useState("grid"); // grid | swipe
  // Phase 5 (2026-07-27): replaced the Any-stars / Any-board / Max-
  // price chip row with the app's `PhaseFourFiltersSheet` per user
  // request. Filter state now maps to the sheet's shape.
  const [filters, setFilters] = useState({
    vibes: [],
    minBudget: "",
    maxBudget: "",
    priceUnit: "per_night",
    propertyTypes: [],
    city: "",
    state: "",
    country: "",
    radius: null,
  });
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    // Only destinationCode is truly required — everything else has
    // sensible defaults now (see the URL-param parser above).
    if (!destinationCode) {
      setErr("Missing destination — head back to the search page to start over.");
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    setRateLimited(false);
    setFallbackHotels([]);
    setFallbackDestMissed(false);
    api.post("/hotels/availability", {
      destinationCode, checkIn, checkOut, occupancies,
    }).then(({ data }) => {
      if (!alive) return;
      setData(data);
    }).catch((e) => {
      if (!alive) return;
      // 429 = Hotelbeds sandbox quota exhausted → surface a cached
      // hotel grid so the user isn't stuck on an empty state (Phase
      // 5 issue-fix, 2026-07-28). Fetched only after the availability
      // 429 arrives — never eats HB quota.
      if (e.response?.status === 429) {
        setRateLimited(true);
        api.get(`/hotels/featured?destinationCode=${encodeURIComponent(destinationCode)}&limit=12`)
          .then(({ data: fb }) => {
            if (!alive) return;
            setFallbackHotels(fb?.hotels || []);
            setFallbackDestMissed(Boolean(fb?.used_dest_fallback));
          })
          .catch(() => { /* fallback fetch itself failed — leave banner alone */ });
      } else {
        setErr(formatApiErrorDetail(e.response?.data?.detail));
      }
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [destinationCode, checkIn, checkOut, occupancies, reloadNonce]);

  // Phase 5 (2026-07-27): apply the filter-sheet output client-side.
  // The sheet returns a broader schema than HB alone can express;
  // we honor Budget (min/max) and Property Type (mapped to HB
  // categoryCode + selected board codes) — the rest (city / state /
  // vibes / radius) are no-ops against HB availability but the sheet
  // still displays them so users see their intent captured.
  //
  // Phase 5 issue-fix (2026-07-28): when the live availability call
  // 429'd, we render the cached-hotel fallback array here instead.
  // Cached rows carry `minRate: null` — budget filters short-circuit
  // to "keep" for them so the user doesn't end up with an empty
  // grid AGAIN post-fallback.
  const sourceHotels = rateLimited ? fallbackHotels : (data.hotels || []);
  const filteredHotels = useMemo(() => {
    const budgetMin = filters.minBudget ? Number(filters.minBudget) : 0;
    const budgetMax = filters.maxBudget ? Number(filters.maxBudget) : Infinity;
    const wantStarsMin = filters.propertyTypes.includes("boutique") ? 4
      : filters.propertyTypes.includes("resort") ? 4
      : 0;
    const wantBoards = new Set();
    if (filters.propertyTypes.includes("all_inclusive")) wantBoards.add("AI");
    return sourceHotels.filter((h) => {
      const stars = categoryStars(h.categoryCode);
      if (wantStarsMin > 0 && stars < wantStarsMin) return false;
      const price = Number(h.minRate);
      if (Number.isFinite(price)) {
        if (price < budgetMin) return false;
        if (price > budgetMax) return false;
      }
      if (wantBoards.size > 0) {
        const rates = (h.rooms || []).flatMap((r) => r.rates || []);
        if (!rates.some((r) => wantBoards.has(r.boardCode))) return false;
      }
      return true;
    });
  }, [sourceHotels, filters]);

  // Human-readable chip labels for the active filters strip.
  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (filters.minBudget || filters.maxBudget) {
      const range =
        filters.minBudget && filters.maxBudget ? `$${filters.minBudget} – $${filters.maxBudget}` :
        filters.maxBudget ? `≤ $${filters.maxBudget}` :
        `≥ $${filters.minBudget}`;
      chips.push({ key: "budget", label: range });
    }
    filters.propertyTypes.forEach((p) => chips.push({ key: `p_${p}`, label: p.replace(/_/g, " ") }));
    if (filters.city || filters.state || filters.country) {
      chips.push({ key: "loc", label: [filters.city, filters.state, filters.country].filter(Boolean).join(", ") });
    }
    return chips;
  }, [filters]);

  const openHotel = (hotel) => {
    // Phase 5 issue-fix (2026-07-28) — Gap 1: cached-fallback cards
    // don't carry rates (they came from `hotelbeds_hotels_content`,
    // not a live availability response). The old guard bailed out
    // early on `!rate` which meant clicks did nothing during quota-
    // locked days. Now we always navigate — the detail page
    // (`/hotels/{code}`) resolves via cache, and if the user
    // decides to book we fire a fresh availability check there.
    const rate = pickCheapestRate(hotel);
    const params = new URLSearchParams({ checkIn, checkOut });
    if (rate) {
      params.set("rateKey", rate.rateKey);
      params.set("rateType", rate.rateType || "BOOKABLE");
    }
    nav(`/hotels/${hotel.code}?${params.toString()}`, {
      state: { from: location.pathname + location.search },
    });
  };

  return (
    <div data-testid="hotels-results-page" className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-8 pt-6 pb-16">
        <button
          data-testid="hotels-results-back"
          onClick={() => nav(backTarget)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border-2 border-tb-navy/20 font-hand text-tb-navy/70 tb-tap text-sm hover:border-tb-navy"
        >
          <ArrowLeft className="w-4 h-4" /> back
        </button>

        {/* Header — Phase 5 (2026-07-27): dropped italic Playfair
            treatment per user; use upright Playfair for the title so
            it still feels editorial but no longer reads as a decorative
            script. Body copy stays hand-drawn but non-italic. */}
        <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1
              data-testid="hotels-results-title"
              className="text-tb-navy text-3xl md:text-4xl leading-tight font-semibold"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Hotels in {destinationName}
            </h1>
            <p className="mt-1 font-hand text-tb-navy/60 text-sm md:text-base">
              {checkIn} → {checkOut} · {occupancies?.[0]?.adults || 0} adults{occupancies?.[0]?.children ? `, ${occupancies[0].children} children` : ""}
            </p>
          </div>
          <PoweredByHotelbeds />
        </div>

        {/* Toolbar — Phase 5: Grid/Swipe + Filters button. The old
            Any-stars / Any-board / Max-price chip row has been
            removed in favor of the app's PhaseFourFiltersSheet. */}
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full border-2 border-tb-navy/15 p-0.5" role="tablist" aria-label="View mode">
            <button
              type="button"
              data-testid="hotels-view-grid"
              onClick={() => setView("grid")}
              aria-selected={view === "grid"}
              className={"inline-flex items-center gap-1.5 px-3.5 h-8 rounded-full font-hand text-sm " + (view === "grid" ? "bg-tb-navy text-tb-gold-bright" : "text-tb-navy/70")}
            >
              <Grid3x3 className="w-3.5 h-3.5" /> Grid
            </button>
            <button
              type="button"
              data-testid="hotels-view-swipe"
              onClick={() => setView("swipe")}
              aria-selected={view === "swipe"}
              className={"inline-flex items-center gap-1.5 px-3.5 h-8 rounded-full font-hand text-sm " + (view === "swipe" ? "bg-tb-navy text-tb-gold-bright" : "text-tb-navy/70")}
            >
              <Layers className="w-3.5 h-3.5" /> Swipe
            </button>
          </div>

          <button
            type="button"
            data-testid="hotels-filters-open"
            onClick={() => setFiltersOpen(true)}
            className="inline-flex items-center gap-1.5 px-3.5 h-8 rounded-full border-2 border-tb-navy/20 bg-white font-hand text-sm text-tb-navy hover:border-tb-navy tb-tap"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            {activeFilterChips.length > 0 ? `Filters (${activeFilterChips.length})` : "Filters"}
          </button>

          <div className="ml-auto font-hand text-tb-navy/55 text-sm">
            {loading
              ? "…"
              : rateLimited
                ? `${filteredHotels.length} cached hotel${filteredHotels.length === 1 ? "" : "s"}`
                : `${filteredHotels.length} of ${data.hotels?.length || 0} hotels`}
          </div>
        </div>

        {/* Active filter chips row — only shows when user has applied
            filters. Keeps the sheet's decisions visible on scroll. */}
        {activeFilterChips.length > 0 && (
          <div
            data-testid="hotels-active-filters"
            className="mt-3 flex flex-wrap gap-2"
          >
            {activeFilterChips.map((c) => (
              <span
                key={c.key}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-tb-navy/[0.04] border border-tb-navy/15 font-hand text-tb-navy/80 text-xs"
              >
                {c.label}
              </span>
            ))}
            <button
              type="button"
              data-testid="hotels-filters-clear"
              onClick={() => setFilters({
                vibes: [], minBudget: "", maxBudget: "", priceUnit: "per_night",
                propertyTypes: [], city: "", state: "", country: "", radius: null,
              })}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-tb-navy/15 text-tb-navy/60 font-hand text-xs hover:border-tb-navy hover:text-tb-navy tb-tap"
            >
              <XIcon className="w-3 h-3" /> clear
            </button>
          </div>
        )}

        {/* Phase 5 issue-fix (2026-07-28): softened rate-limit
            banner now carries a Retry pill AND surfaces the cached-
            hotel fallback grid below so the user is never stuck on
            an empty state during sandbox quota exhaustion. */}
        {rateLimited && (
          <div
            data-testid="hotels-ratelimit-banner"
            className="mt-4 flex flex-wrap items-start gap-3 rounded-xl bg-tb-navy/[0.03] border border-tb-navy/15 px-4 py-3 font-hand text-tb-navy/85 text-sm"
          >
            <Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-tb-navy/60" />
            <div className="flex-1 min-w-0">
              <div>
                Hotelbeds is temporarily rate-limiting us in sandbox mode.
                Showing <b>{fallbackHotels.length} cached hotel{fallbackHotels.length === 1 ? "" : "s"}</b>
                {fallbackDestMissed ? " (no cache for this destination — showing the freshest we have)" : ` for ${destinationName || destinationCode}`}
                {" "}so you can still explore.
              </div>
            </div>
            <button
              type="button"
              data-testid="hotels-ratelimit-retry"
              onClick={() => setReloadNonce((n) => n + 1)}
              className="inline-flex items-center gap-1.5 rounded-full bg-tb-navy text-tb-gold-bright px-3 h-8 font-hand text-xs tb-tap hover:brightness-110 flex-shrink-0"
            >
              <RefreshCw className={"w-3.5 h-3.5 " + (loading ? "animate-spin" : "")} />
              Retry
            </button>
          </div>
        )}
        {err && !rateLimited && (
          <div data-testid="hotels-results-error" className="mt-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 font-hand text-red-700 text-sm">
            {err}
          </div>
        )}

        {loading ? (
          <p className="mt-16 text-center font-hand text-tb-navy/60">~ finding hotels ~</p>
        ) : filteredHotels.length === 0 ? (
          <div data-testid="hotels-results-empty" className="mt-14 text-center py-14 rounded-2xl border-2 border-tb-navy/10 bg-white">
            <Bed className="w-12 h-12 text-tb-navy/25 mx-auto" strokeWidth={1.4} />
            <p className="mt-3 text-tb-navy text-xl font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
              No hotels match those filters.
            </p>
            <p className="mt-1 font-hand text-tb-navy/60 text-sm">Try widening your criteria or picking different dates.</p>
          </div>
        ) : view === "swipe" ? (
          <div className="mt-8">
            <SwipeDeck hotels={filteredHotels} onOpen={openHotel} />
          </div>
        ) : (
          <ul data-testid="hotels-results-grid" className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredHotels.slice(0, 60).map((h) => (
              <li key={h.code}>
                <HotelCard hotel={h} onClick={() => openHotel(h)} />
              </li>
            ))}
          </ul>
        )}

        {/* Filters sheet — mounts lazily; on Apply commits to
            `filters` state which drives the client-side reduce
            above. Reset clears and closes. */}
        {filtersOpen && (
          <PhaseFourFiltersSheet
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            value={filters}
            onApply={(draft) => {
              setFilters(draft);
              setFiltersOpen(false);
            }}
            onReset={() => {
              setFilters({
                vibes: [], minBudget: "", maxBudget: "", priceUnit: "per_night",
                propertyTypes: [], city: "", state: "", country: "", radius: null,
              });
              setFiltersOpen(false);
            }}
          />
        )}
      </div>
    </div>
  );
}
